<script setup>
import { useTemplateRef, onMounted, onUnmounted, watch } from 'vue'
import { Map, View } from 'ol'
import TileLayer from 'ol/layer/Tile'
import OSM from 'ol/source/OSM'
import { fromLonLat } from 'ol/proj'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import { Icon, Style } from 'ol/style'

const { coordinates, zoom = 16 } = defineProps({
  coordinates: {
    type: Array,
    required: true,
  },
  zoom: {
    type: Number,
    default: 16,
  },
})

const mapContainer = useTemplateRef('mapContainer')
let map = null
let markerSource = null

// Custom glowing marker SVG - elegant blue with soft glow effect
const glowingMarkerSvg = `
<svg width="48" height="56" viewBox="0 0 48 56" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="pinGradient" x1="12" y1="8" x2="36" y2="48" gradientUnits="userSpaceOnUse">
      <stop stop-color="#60A5FA"/>
      <stop offset="0.5" stop-color="#3B82F6"/>
      <stop offset="1" stop-color="#1D4ED8"/>
    </linearGradient>
    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
      <feMerge>
        <feMergeNode in="coloredBlur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
    <filter id="shadow" x="-50%" y="-30%" width="200%" height="200%">
      <feDropShadow dx="0" dy="4" stdDeviation="4" flood-color="#1E40AF" flood-opacity="0.35"/>
    </filter>
  </defs>
  <g filter="url(#shadow)">
    <g filter="url(#glow)">
      <path d="M24 4C14.059 4 6 12.059 6 22c0 13 18 30 18 30s18-17 18-30c0-9.941-8.059-18-18-18z" fill="url(#pinGradient)"/>
      <circle cx="24" cy="20" r="7" fill="white" fill-opacity="0.95"/>
      <circle cx="24" cy="20" r="3" fill="#3B82F6"/>
    </g>
  </g>
</svg>
`

const getMapCoords = (coords) => {
  // Coordinates come as [lat, lon] in WGS84 from the backend
  // OpenLayers fromLonLat expects [lon, lat]
  if (!coords || coords.length < 2) return fromLonLat([24.7536, 59.437]) // Default to Tallinn
  return fromLonLat([coords[1], coords[0]])
}

// View center offset: shift view so marker appears on left third and at bottom edge
const getViewCenter = (coords) => {
  const mapCoords = getMapCoords(coords)
  // Offset view center: right (east) +400m, up (north) +320m
  // This positions marker in left third, at very bottom (below price card, aligned with address)
  return [mapCoords[0] + 240, mapCoords[1] + 430]
}

const createMarkerStyle = () => {
  return new Style({
    image: new Icon({
      anchor: [0.5, 1],
      src: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(glowingMarkerSvg),
      scale: 1,
    }),
  })
}

const initMap = () => {
  if (!mapContainer.value) return

  const mapCoords = getMapCoords(coordinates)
  const viewCenter = getViewCenter(coordinates)

  markerSource = new VectorSource()

  const markerFeature = new Feature({
    geometry: new Point(mapCoords),
  })

  markerFeature.setStyle(createMarkerStyle())
  markerSource.addFeature(markerFeature)

  map = new Map({
    target: mapContainer.value,
    layers: [
      new TileLayer({
        source: new OSM({
          crossOrigin: 'anonymous',
        }),
      }),
      new VectorLayer({
        source: markerSource,
        zIndex: 10,
      }),
    ],
    view: new View({
      center: viewCenter,
      zoom: zoom,
      maxZoom: 19,
      minZoom: 12,
    }),
    controls: [],
    interactions: [], // No interactions - purely decorative
  })
}

watch(
  () => coordinates,
  newCoords => {
    if (map && newCoords) {
      const mapCoords = getMapCoords(newCoords)
      const viewCenter = getViewCenter(newCoords)
      map.getView().animate({ center: viewCenter, duration: 800 })
      markerSource.clear()
      const newMarker = new Feature({
        geometry: new Point(mapCoords),
      })
      newMarker.setStyle(createMarkerStyle())
      markerSource.addFeature(newMarker)
    }
  },
  { deep: true }
)

onMounted(() => {
  initMap()
})

onUnmounted(() => {
  if (map) {
    map.dispose()
    map = null
  }
})
</script>

<template>
  <div class="map-background-container absolute inset-0 overflow-hidden">
    <!-- Map element with desaturated filter -->
    <div ref="mapContainer" class="map-element absolute inset-0 w-full h-full"></div>

    <!-- Cinematic gradient overlay - fades to page background -->
    <div class="absolute inset-0 pointer-events-none map-overlay"></div>

    <!-- Subtle vignette for depth -->
    <div class="absolute inset-0 pointer-events-none vignette"></div>
  </div>
</template>

<style scoped>
.map-element {
  filter: grayscale(35%) brightness(1.02) contrast(0.95);
}

:deep(.ol-viewport) {
  border-radius: 0;
}

.map-overlay {
  background: linear-gradient(
    to bottom,
    rgba(248, 250, 252, 0) 0%,
    rgba(248, 250, 252, 0) 40%,
    rgba(248, 250, 252, 0.05) 55%,
    rgba(248, 250, 252, 0.15) 65%,
    rgba(248, 250, 252, 0.35) 75%,
    rgba(248, 250, 252, 0.6) 85%,
    rgba(248, 250, 252, 0.85) 92%,
    rgb(255, 255, 255) 100%
  );
}

.vignette {
  background: radial-gradient(
    ellipse at center,
    transparent 0%,
    transparent 50%,
    rgba(15, 23, 42, 0.03) 100%
  );
}
</style>
